// hooks/useStatusSocket.js

import { useEffect, useCallback, useRef } from "react";

import { uploadMedia } from "../utils/MediaUtils";

import {
  generateStatusKey,
  exportStatusKey,

  decryptStatusKey,
  importStatusKey,
  cacheStatusKey,
  getCachedStatusKey,
  loadPrivateKey,

  decryptWithStatusKey,
} from "../utils/Cryptoutils";

import {
  encryptStatus,
  encryptStatusCaption,
  encryptStatusMedia,
  encryptStatusThumbnail,
} from "../utils/encrypt";

import {
  saveStatus,
  deleteStatus as deleteStatusFromDB,
  saveStatusCryptoKey,
  loadStatusCryptoKey,
} from "../db/fsDB";

/* =====================================================
   HELPER — decrypt a single ciphertext envelope array
   (statuses store fields as single-element arrays, e.g.
   encryptedText: [{ ciphertext, iv }])
===================================================== */
const decryptEnvelopeArray = async (arr, cryptoKey) => {

  if (!arr || !arr[0] || !cryptoKey) {
    return null;
  }

  const { ciphertext, iv } = arr[0];

  if (!ciphertext || !iv) {
    return null;
  }

  return decryptWithStatusKey(
    ciphertext,
    iv,
    cryptoKey
  );

};

export default function useStatusSocket({

  socket,

  myChatId,

  setStatusNotifications = () => {},

  setMyStatusIds = () => {},

  onViewsUpdated = () => {},

  setHasUnreadStatus = () => {},

}) {

  /* =====================================================
     PENDING status:get-key REQUESTS
     Maps ownerChatId -> { resolve, timeout } so multiple
     callers (this hook + Status.js) can await the same
     in-flight request instead of firing duplicates.
  ===================================================== */
  const pendingKeyRequestsRef = useRef(new Map());

  const resolvePendingKeyRequest = useCallback(
    (ownerChatId, key) => {

      const pending =
        pendingKeyRequestsRef.current.get(ownerChatId);

      if (!pending) {
        return;
      }

      clearTimeout(pending.timeout);

      pending.resolve(key);

      pendingKeyRequestsRef.current.delete(ownerChatId);

    },
    []
  );

  /* =====================================================
     RESOLVE STATUS KEY
     cache -> IDB -> server round trip (status:get-key)
  ===================================================== */
  const resolveStatusKey = useCallback(

    async (statusId, ownerChatId) => {

      if (!statusId || !ownerChatId) {
        return null;
      }

      // 1. In-memory cache
      const cached = getCachedStatusKey(ownerChatId);

      if (cached) {
        return cached;
      }

      // 2. IndexedDB
      try {

        const idbKey =
          await loadStatusCryptoKey(ownerChatId);

        if (idbKey) {

          cacheStatusKey(ownerChatId, idbKey);

          return idbKey;

        }

      } catch (err) {
        // ignore — fall through to server round trip
      }

      // 3. Ask the server and wait for status:key
      if (!socket) {
        return null;
      }

      // Reuse an in-flight request if one exists
      if (pendingKeyRequestsRef.current.has(ownerChatId)) {

        return new Promise((resolve) => {

          const existing =
            pendingKeyRequestsRef.current.get(ownerChatId);

          // piggyback: wrap resolve so both callers get notified
          const originalResolve = existing.resolve;

          existing.resolve = (key) => {
            originalResolve(key);
            resolve(key);
          };

        });

      }

      return new Promise((resolve) => {

        const timeout = setTimeout(() => {

          pendingKeyRequestsRef.current.delete(ownerChatId);

          resolve(null);

        }, 10000);

        pendingKeyRequestsRef.current.set(ownerChatId, {
          resolve,
          timeout,
        });

        // The backend still resolves the owner from the status,
        // so we only need to send statusId here.
        socket.emit(
          "status:get-key",
          { statusId }
        );

      });

    },
    [socket]
  );

  /* =====================================================
     DECRYPT STATUS FOR VIEWING
     Public helper — Status.js / its parent can call this
     directly when a status is opened.
  ===================================================== */
  const decryptStatusForViewing = useCallback(

    async (status) => {

      if (!status?.statusId) {
        return null;
      }

      const cryptoKey =
        await resolveStatusKey(
          status.statusId,
          status.ownerChatId
        );

      if (!cryptoKey) {
        return null;
      }

      try {

        const [
          decryptedText,
          decryptedMediaUrl,
          decryptedCaption,
          decryptedThumbnail,
        ] = await Promise.all([

          decryptEnvelopeArray(
            status.encryptedText,
            cryptoKey
          ),

          decryptEnvelopeArray(
            status.encryptedMedia,
            cryptoKey
          ),

          decryptEnvelopeArray(
            status.caption,
            cryptoKey
          ),

          decryptEnvelopeArray(
            status.thumbnailEncrypted,
            cryptoKey
          ),

        ]);

        return {
          decryptedText,
          decryptedMediaUrl,
          decryptedCaption,
          decryptedThumbnail,
        };

      } catch (err) {

        return null;

      }

    },
    [resolveStatusKey]
  );

  /* =====================================================
     CREATE STATUS
  ===================================================== */

  const createStatus = useCallback(
    async (payload = {}) => {

      if (!socket) {
        return;
      }

      const {

        ownerChatId,

        statusId,

        statusType = "text",

        text = null,

        media = null,

        caption = null,

        thumbnail = null,

        mediaMimeType = null,

        mediaSize = null,

        mediaDuration = null,

        backgroundColor = null,

      } = payload;

      if (!ownerChatId || !statusId) {
        return;
      }

      let uploadedMedia = null;

      if (media instanceof File) {

        uploadedMedia = await uploadMedia(
          media,
          {
            statusId,
            type: "status",
          }
        );

      }

      /* =========================================
         RESOLVE STATUS KEY
         Check IDB first (retry-safe / idempotent),
         otherwise generate a fresh shared AES-GCM
         key. One persistent key per owner — follower
         copies are ECDH-wrapped server-side on demand
         via status:get-key.
      ========================================= */

      let statusCryptoKey =
        await loadStatusCryptoKey(ownerChatId);

      if (statusCryptoKey) {

        // reusing existing status key

      } else {

        statusCryptoKey =
          await generateStatusKey();

        if (!statusCryptoKey) {

          return;

        }

        await saveStatusCryptoKey(
          ownerChatId,
          statusCryptoKey
        );

      }

      cacheStatusKey(ownerChatId, statusCryptoKey);

      const statusKeyBase64 =
        await exportStatusKey(
          statusCryptoKey
        );

      if (!statusKeyBase64) {

        return;

      }

      const keyVersion = 1;

      const [

        encryptedText,

        encryptedMedia,

        encryptedCaption,

        encryptedThumbnail,

      ] = await Promise.all([

        text
          ? encryptStatus(
              text,
              statusCryptoKey
            )
          : null,

        uploadedMedia?.url
          ? encryptStatusMedia(
              uploadedMedia.url,
              statusCryptoKey
            )
          : null,

        caption
          ? encryptStatusCaption(
              caption,
              statusCryptoKey
            )
          : null,

        thumbnail
          ? encryptStatusThumbnail(
              thumbnail,
              statusCryptoKey
            )
          : null,

      ]);

/* =========================================
   OPTIMISTIC REACT UPDATE
   Show the status immediately before
   socket emit and before IndexedDB.
========================================= */

setStatusNotifications((prev) => {

  if (
    prev.some(
      (s) => s.statusId === statusId
    )
  ) {
    return prev;
  }

  return [

    {

      statusId,

      ownerChatId,

      statusType,

      encryptedText:
        encryptedText
          ? [encryptedText]
          : [],

      encryptedMedia:
        encryptedMedia
          ? [encryptedMedia]
          : [],

      caption:
        encryptedCaption
          ? [encryptedCaption]
          : [],

      thumbnailEncrypted:
        encryptedThumbnail
          ? [encryptedThumbnail]
          : [],

      mediaMimeType,

      mediaSize,

      mediaDuration,

      backgroundColor,

      // Plaintext for immediate UI rendering
      decryptedText: text,

      decryptedMediaUrl:
        uploadedMedia?.url ?? null,

      decryptedCaption:
        caption,

      decryptedThumbnail:
        thumbnail,

      createdAt: new Date(),

      expiresAt: new Date(
        Date.now() + 24 * 60 * 60 * 1000
      ),

      viewsCount: 0,

      isDeleted: false,

      optimistic: true,

    },

    ...prev,

  ];

});

      socket.emit(
        "status:create",
        {

          statusId,

          statusType,

          encryptedText:
            encryptedText
              ? [encryptedText]
              : [],

          encryptedMedia:
            encryptedMedia
              ? [encryptedMedia]
              : [],

          caption:
            encryptedCaption
              ? [encryptedCaption]
              : [],

          thumbnailEncrypted:
            encryptedThumbnail
              ? [encryptedThumbnail]
              : [],

          mediaMimeType,

          mediaSize,

          mediaDuration,

          backgroundColor,

          mediaUrl: uploadedMedia?.url ?? null,

          statusKey: statusKeyBase64,

          keyVersion,

        }
      );

      // NOTE: only ciphertext is persisted here — no plaintext
      // text/media/caption/thumbnail fields are written to IndexedDB.
      // The creating device still has the plaintext in memory (payload)
      // and can render immediately without waiting on this save.
      await saveStatus({

        statusId,

        ownerChatId,

        statusType,

        encryptedText:
          encryptedText
            ? [encryptedText]
            : [],

        encryptedMedia:
          encryptedMedia
            ? [encryptedMedia]
            : [],

        caption:
          encryptedCaption
            ? [encryptedCaption]
            : [],

        thumbnailEncrypted:
          encryptedThumbnail
            ? [encryptedThumbnail]
            : [],

        mediaMimeType,

        mediaSize,

        mediaDuration,

        backgroundColor,

        viewsCount: 0,

        isDeleted: false,

        createdAt: new Date(),

        expiresAt: new Date(
          Date.now() + 24 * 60 * 60 * 1000
        ),

      });

    },
    [socket]
  );

  /* =====================================================
     DELETE STATUS
  ===================================================== */

  const deleteStatus = useCallback(
    async (statusId) => {

      if (!socket) {
        return;
      }

      socket.emit(
        "status:delete",
        {
          statusId,
        }
      );

      await deleteStatusFromDB(statusId);

    },
    [socket]
  );

  /* =====================================================
     VIEW STATUS
  ===================================================== */

  const viewStatus = useCallback(
    (statusId) => {

      if (!socket) {
        return;
      }

      socket.emit(
        "status:view",
        {
          statusId,
        }
      );

    },
    [socket]
  );

  /* =====================================================
     STATUS PAGE OPENED

     Grey out the Status tab.
  ===================================================== */

  const markStatusBadgeRead =
    useCallback(() => {

      if (!socket) {
        return;
      }

      socket.emit(
        "status:badge-read"
      );

    }, [socket]);

  /* =====================================================
     GET STATUS KEY (fire-and-forget — kept for
     backwards compatibility with existing callers).
     Prefer resolveStatusKey / decryptStatusForViewing
     for anything that needs the resulting key.
  ===================================================== */

  const getStatusKey = useCallback(

    (statusId) => {

      if (!socket || !statusId) {
        return;
      }

      socket.emit(
        "status:get-key",
        {
          statusId,
        }
      );

    },

    [socket]

  );

  /* =====================================================
     REPLY TO STATUS
  ===================================================== */

  const sendStatusReply = useCallback(
    (statusId, ownerChatId, text) => {

      if (!socket) {
        return;
      }
      if (!statusId || !ownerChatId || !text?.trim()) {
        return;
      }

      socket.emit(
        "status:reply",
        {
          statusId,

          ownerChatId,

          text: text.trim(),
        }
      );

    },
    [socket]
  );

  /* =====================================================
     SOCKET EVENTS
  ===================================================== */

  useEffect(() => {

    if (!socket) {
      return;
    }

    /* ---------------------------------- */

    const onCreated = ({
      statusId,
    }) => {

      setMyStatusIds(
        (prev) => {

          if (
            prev.includes(statusId)
          ) {
            return prev;
          }

          return [
            statusId,
            ...prev,
          ];

        }
      );

    };

    /* ---------------------------------- */

    const onDeleted = ({
      statusId,
    }) => {

      setMyStatusIds(
        (prev) =>
          prev.filter(
            (id) =>
              id !== statusId
          )
      );

      deleteStatusFromDB(statusId);

    };

    /* ---------------------------------- */

    // Server emits the full ciphertext payload on status:new (mirrors
    // what's stored in Mongo). Applies to BOTH the owner's other devices
    // and followers — neither has the status key yet, so both take the
    // same path: save the encrypted status, then resolve the key
    // (cache -> IDB -> status:get-key round trip) and decrypt for the UI.
   
    const onNewStatus = async ({

  statusId,

  ownerChatId,

  statusType,

  createdAt,

  expiresAt,

  encryptedText = [],

  encryptedMedia = [],

  caption = [],

  thumbnailEncrypted = [],

  mediaMimeType = null,

  mediaSize = null,

  mediaDuration = null,

  backgroundColor = null,

}) => {

  /* =========================================
     DECRYPT FIRST
     cache -> IDB -> status:get-key ->
     decrypt wrapped key -> decrypt status
  ========================================= */

  let decrypted = null;

  try {

    decrypted =
      await decryptStatusForViewing({

        statusId,

        ownerChatId,

        encryptedText,

        encryptedMedia,

        caption,

        thumbnailEncrypted,

      });

  } catch (err) {

    // ignore — decrypted stays null

  }

  /* =========================================
     UPDATE REACT STATE IMMEDIATELY
  ========================================= */

  setStatusNotifications((prev) => {

    const existing =
      prev.find(
        (status) =>
          status.statusId === statusId
      );

    if (existing) {

      return prev.map((status) =>
        status.statusId === statusId
          ? {
              ...status,

              ownerChatId,

              statusType,

              createdAt,

              expiresAt,

              encryptedText,

              encryptedMedia,

              caption,

              thumbnailEncrypted,

              mediaMimeType,

              mediaSize,

              mediaDuration,

              backgroundColor,

              ...(decrypted || {}),
            }
          : status
      );

    }

    return [

      {

        statusId,

        ownerChatId,

        statusType,

        createdAt,

        expiresAt,

        encryptedText,

        encryptedMedia,

        caption,

        thumbnailEncrypted,

        mediaMimeType,

        mediaSize,

        mediaDuration,

        backgroundColor,

        viewsCount: 0,

        isDeleted: false,

        ...(decrypted || {}),

      },

      ...prev,

    ];

  });

  /* =========================================
     SAVE ENCRYPTED COPY TO IDB
     (background only)
  ========================================= */

  saveStatus({

    statusId,

    ownerChatId,

    statusType,

    encryptedText,

    encryptedMedia,

    caption,

    thumbnailEncrypted,

    mediaMimeType,

    mediaSize,

    mediaDuration,

    backgroundColor,

    viewsCount: 0,

    isDeleted: false,

    createdAt:
      createdAt
        ? new Date(createdAt)
        : new Date(),

    expiresAt:
      expiresAt
        ? new Date(expiresAt)
        : new Date(
            Date.now() +
            24 * 60 * 60 * 1000
          ),

  })
  .then(() => {

    // saved

  })
  .catch((err) => {

    // ignore save error

  });

};
    /* ---------------------------------- */

    const onStatusKey = async ({
      statusId,
      ownerChatId,
      encryptedStatusKey,
    }) => {

      try {

        const privateKey =
          await loadPrivateKey(
            myChatId
          );

        if (!privateKey) {
          resolvePendingKeyRequest(ownerChatId, null);
          return;
        }

        const rawKey =
          await decryptStatusKey(
            encryptedStatusKey,
            privateKey
          );

        if (!rawKey) {
          resolvePendingKeyRequest(ownerChatId, null);
          return;
        }

        const cryptoKey =
          await importStatusKey(
            rawKey
          );

        if (!cryptoKey) {
          resolvePendingKeyRequest(ownerChatId, null);
          return;
        }

        cacheStatusKey(
          ownerChatId,
          cryptoKey
        );

        await saveStatusCryptoKey(
          ownerChatId,
          cryptoKey
        );

        resolvePendingKeyRequest(ownerChatId, cryptoKey);

      } catch (err) {

        resolvePendingKeyRequest(ownerChatId, null);

      }

    };

    /* ---------------------------------- */

    // Server emits this to the owner's room whenever a follower
    // views one of the owner's statuses (see status:view on the
    // backend). Surface it via the optional onViewsUpdated callback
    // so the parent can bump a local viewsCount if it wants to.
   
const onStatusViewed = ({
  statusId,
  viewerChatId,
  viewsCount,
}) => {

  /* React */

  setStatusNotifications((prev) =>
    prev.map((status) =>
      status.statusId === statusId
        ? {
            ...status,
            viewsCount,
          }
        : status
    )
  );

  /* IndexedDB */

  saveStatus({
    statusId,
    viewsCount,
  }).catch(() => {});

  onViewsUpdated(
    statusId,
    viewerChatId,
    viewsCount
  );

};
    /* ---------------------------------- */

    const onStatusError = (err = {}) => {

      // Unblock any pending status:get-key request tied to this error
      if (
        err.action === "get-key" &&
        err.ownerChatId
      ) {
        resolvePendingKeyRequest(
          err.ownerChatId,
          null
        );
      }

    };

    /* ---------------------------------- */

    const onStatusBadge = ({
      hasUnread,
    }) => {

      setHasUnreadStatus(
        hasUnread
      );

    };

    /* ---------------------------------- */

    socket.on(
      "status:created",
      onCreated
    );

    socket.on(
      "status:new",
      onNewStatus
    );

    socket.on(
      "status:deleted",
      onDeleted
    );

    socket.on(
      "status:key",
      onStatusKey
    );

    socket.on(
      "status:viewed",
      onStatusViewed
    );

    socket.on(
      "status:error",
      onStatusError
    );

    socket.on(
      "status:badge",
      onStatusBadge
    );

    return () => {

      socket.off(
        "status:created",
        onCreated
      );

      socket.off(
        "status:new",
        onNewStatus
      );

      socket.off(
        "status:deleted",
        onDeleted
      );

      socket.off(
        "status:key",
        onStatusKey
      );

      socket.off(
        "status:viewed",
        onStatusViewed
      );

      socket.off(
        "status:error",
        onStatusError
      );

      socket.off(
        "status:badge",
        onStatusBadge
      );

    };

  }, [
    socket,
    myChatId,
    setStatusNotifications,
    setMyStatusIds,
    setHasUnreadStatus,
    onViewsUpdated,
    decryptStatusForViewing,
    resolvePendingKeyRequest,
  ]);

  /* =====================================================
     EXPORTS
  ===================================================== */

  return {

    createStatus,

    deleteStatus,

    viewStatus,

    markStatusBadgeRead,

    getStatusKey,

    sendStatusReply,

    resolveStatusKey,

    decryptStatusForViewing,

  };

}